<template>
    <div class="header-area default-header relative z-50 bg-white dark:bg-title" :class="scroll ? 'sticky-header' : ''">
        <div class="container-fluid">
            <div class="flex items-center justify-between gap-x-6 max-w-[1720px] mx-auto relative py-[10px] sm:py-4 lg:py-0">
                <!-- Word-mark rather than the template's logo file, which carried the
                     template vendor's brand. Swap in a real logo image when there is one. -->
                <router-link class="cursor-pointer block py-3 sm:py-4" to="/index-v2" :aria-label="company.name">
                    <span class="block font-semibold leading-none text-title dark:text-white text-[18px] sm:text-[24px] tracking-tight">
                        {{ company.name }}
                    </span>
                    <span class="mt-1 hidden sm:block text-[11px] uppercase tracking-[0.22em] text-primary">
                        {{ company.shortTagline }}
                    </span>
                </router-link>

                <div class="main-menu absolute z-50 w-full lg:w-auto top-full left-0 lg:static bg-white dark:bg-title lg:bg-transparent lg:dark:bg-transparent px-5 sm:px-[30px] py-[10px] sm:py-5 lg:px-0 lg:py-0" :class="toggle ? 'active' : ''">
                    <ul class="text-lg leading-none text-title dark:text-white lg:flex lg:gap-[30px]">
                        <li class="relative" :class="current === '/index-v2' ? 'active' : ''">
                            <router-link to="/index-v2">Home</router-link>
                        </li>
                        <li :class="['/about','/our-clients','/faq','/terms-and-conditions','/request-quotation','/my-profile','/my-account','/order-history','/my-requests','/wishlist','/login','/register'].includes(current) ? 'active' : ''">
                            <!-- Opens the dropdown; it is not itself a destination. -->
                            <button type="button" class="cursor-default">Pages<span></span></button>
                            <div class="mega-menu lg:absolute z-50 lg:top-full lg:left-1/2 lg:transform lg:-translate-x-1/2 lg:max-w-[760px] lg:w-full lg:bg-white lg:dark:bg-title lg:px-[30px] lg:py-[15px] lg:flex lg:items-start lg:justify-between gap-[30px] lg:invisible lg:transition-all lg:duration-300">
                                <div class="lg:grid lg:grid-cols-2 lg:items-start gap-x-10 gap-y-4 lg:flex-1">
                                    <div class="megamenu-item">
                                        <ul>
                                            <li :class="current === '/about' ? 'active' : ''"><router-link to="/about">About Us</router-link></li>
                                            <li :class="current === '/faq' ? 'active' : ''"><router-link to="/faq">FAQs</router-link></li>
                                            <li :class="current === '/terms-and-conditions' ? 'active' : ''"><router-link to="/terms-and-conditions">Terms & conditions</router-link></li>
                                            <li :class="current === '/request-quotation' ? 'active' : ''"><router-link to="/request-quotation">Request a Quotation</router-link></li>
                                        </ul>
                                    </div>
                                    <div class="megamenu-item">
                                        <ul>
                                            <li v-if="isAuthenticated" :class="current === '/my-account' ? 'active' : ''"><router-link to="/my-account">My Account</router-link></li>
                                            <li v-if="isAuthenticated" :class="current.startsWith('/order-history') ? 'active' : ''"><router-link to="/order-history">My Orders</router-link></li>
                                            <li v-if="isAuthenticated" :class="current === '/my-requests' ? 'active' : ''"><router-link to="/my-requests">My Requests</router-link></li>
                                            <li v-if="isAuthenticated" :class="current === '/wishlist' ? 'active' : ''"><router-link to="/wishlist">Wishlist</router-link></li>
                                            <li v-if="isAuthenticated"><button type="button" class="duration-300 hover:text-primary" @click="handleLogout">Logout</button></li>
                                            <li v-if="!isAuthenticated" :class="current === '/login' ? 'active' : ''"><router-link to="/login">Login</router-link></li>
                                            <li v-if="!isAuthenticated" :class="current === '/register' ? 'active' : ''"><router-link to="/register">Register</router-link></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </li>
                        <li class="relative" :class="['/shop-v2','/product-details','/cart','/checkout'].includes(current) ? 'active' : ''">
                            <router-link to="/shop-v2">Shop</router-link>
                        </li>
                        <li :class="current.startsWith('/portfolio') ? 'active' : ''"><router-link to="/portfolio-v1">Our Work</router-link></li>
                        <li :class="current === '/assistant' ? 'active' : ''">
                            <router-link to="/assistant" class="nav-ai">
                                <svg class="nav-ai__icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" />
                                    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
                                </svg>
                                AI Assistant
                            </router-link>
                        </li>
                        <li :class="current === '/contact' ? 'active' : ''"><router-link to="/contact">Contact</router-link></li>
                        <li v-if="isAuthenticated" :class="current === '/my-account' ? 'active' : ''" class="lg:hidden"><router-link to="/my-account">My Account</router-link></li>
                        <li v-else :class="current === '/login' ? 'active' : ''" class="lg:hidden"><router-link to="/login">Login</router-link></li>
                    </ul>
                </div>

                <NavMenuOne :toggle="toggle" @toggle-change="toggle = $event"/>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';

import { company } from '@/data/company';

import NavMenuOne from './nav-menu-one.vue';
import { useRoute, useRouter } from 'vue-router';
import { useClientSession } from '@/composables/use-client-session';

const toggle = ref(false);
const session = useClientSession();
const isAuthenticated = computed(() => session.isAuthenticated.value);

const router = useRoute();
const current = ref(router.path);
const navigation = useRouter();

// Signing out used to be reachable only from inside the account pages.
async function handleLogout() {
    await session.logout();
    toggle.value = false;
    navigation.push('/login');
}

const scroll = ref(false)

const handleScroll = () => {
    if (window.scrollY >= 50) {
        scroll.value = true
    } else {
        scroll.value = false
    }
}

onMounted(()=>{
    window.scrollTo(0,0)
    window.addEventListener('scroll', handleScroll)
    session.initializeSession().catch(() => {})
})

</script>

<style>
.main-menu a.nav-ai { display: flex; align-items: center; justify-content: flex-start; gap: 6px; width: auto; }
@media (min-width: 1024px) { .main-menu a.nav-ai { display: inline-flex; } }
.nav-ai__icon { color: #bb976d; flex: 0 0 auto; margin-top: -2px; }
.main-menu li.active .nav-ai, .nav-ai:hover { color: #bb976d; }
</style>
